import { useCallback, useEffect, useState } from "react";
import { ApiError, api } from "../../api/client";
import DropdownSelect from "../../components/DropdownSelect";
import type { Site } from "../../types";

interface Cycle {
  id: number;
  site_id: number;
  name: string;
  cycle_type: string;
  description: string | null;
  is_active: boolean;
}
interface Period {
  id: number;
  cycle_id: number;
  period_start: string;
  period_end: string;
  pay_date: string;
  status: string;
  exported_at: string | null;
}

export default function AdminPayrollPage() {
  const [cycles, setCycles] = useState<Cycle[]>([]);
  const [sites, setSites] = useState<Site[]>([]);
  const [formCycle, setFormCycle] = useState<Cycle | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [periods, setPeriods] = useState<Period[]>([]);
  const [deleteTarget, setDeleteTarget] = useState<Cycle | null>(null);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    try {
      const [c, s] = await Promise.all([
        api.get<Cycle[]>("/api/payroll/cycles"),
        api.get<Site[]>("/api/sites"),
      ]);
      setCycles(c);
      setSites(s);
    } catch {
      /* swallow */
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const loadPeriods = async (cycleId: number) => {
    try {
      setPeriods(
        await api.get<Period[]>(`/api/payroll/periods?cycle_id=${cycleId}`),
      );
    } catch {
      setPeriods([]);
    }
  };

  const toggle = (id: number) => {
    if (expandedId === id) {
      setExpandedId(null);
    } else {
      setExpandedId(id);
      void loadPeriods(id);
    }
  };

  const openCreate = () => {
    setFormCycle(null);
    setShowForm(true);
  };

  const openEdit = (c: Cycle) => {
    setFormCycle(c);
    setShowForm(true);
  };

  const handleSaved = (savedCycle: Cycle) => {
    setShowForm(false);
    setFormCycle(null);
    void load();
    setExpandedId(savedCycle.id);
    void loadPeriods(savedCycle.id);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await api.delete(`/api/payroll/cycles/${deleteTarget.id}`);
      setDeleteTarget(null);
      if (expandedId === deleteTarget.id) setExpandedId(null);
      void load();
    } catch {
      /* swallow */
    } finally {
      setDeleting(false);
    }
  };

  const markPaid = async (periodId: number, cycleId: number) => {
    await api.put(`/api/payroll/periods/${periodId}/mark-paid`);
    void loadPeriods(cycleId);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Payroll</h1>
          <p className="text-sm text-gray-500">
            Configure payroll cycles and export period data.
          </p>
        </div>
        <button
          type="button"
          onClick={openCreate}
          className="rounded-lg bg-pme-blue px-4 py-2 text-sm font-semibold text-white"
        >
          + Create Cycle
        </button>
      </div>

      {showForm && (
        <CycleForm
          cycle={formCycle}
          sites={sites}
          onSaved={handleSaved}
          onCancel={() => {
            setShowForm(false);
            setFormCycle(null);
          }}
        />
      )}

      {cycles.length === 0 && !showForm && (
        <p className="text-sm text-gray-400">No payroll cycles configured.</p>
      )}

      {cycles.map((c) => {
        const cyclePeriods = periods.filter((p) => p.cycle_id === c.id);
        return (
          <div
            key={c.id}
            className="rounded-xl border border-gray-200 bg-white shadow-sm"
          >
            <div className="flex items-center justify-between px-5 py-4">
              <button
                type="button"
                onClick={() => toggle(c.id)}
                className="flex-1 text-left"
              >
                <p className="text-sm font-semibold text-gray-900">{c.name}</p>
                <p className="text-xs text-gray-500">
                  {siteName(c.site_id, sites)} · {c.cycle_type}
                  {c.description ? ` · ${c.description}` : ""}
                </p>
              </button>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => openEdit(c)}
                  className="rounded-lg border border-gray-300 px-3 py-1.5 text-xs text-gray-600 hover:bg-gray-50"
                >
                  Edit
                </button>
                <button
                  type="button"
                  onClick={() => setDeleteTarget(c)}
                  className="rounded-lg border border-red-200 px-3 py-1.5 text-xs text-red-600 hover:bg-red-50"
                >
                  Delete
                </button>
                <span className="ml-1 text-gray-400">
                  {expandedId === c.id ? "▾" : "▸"}
                </span>
              </div>
            </div>
            {expandedId === c.id && (
              <div className="border-t border-gray-100 px-5 py-4">
                <div className="overflow-x-auto rounded-lg border border-gray-100">
                  <table className="min-w-full text-sm">
                    <thead className="bg-gray-50 text-left text-xs uppercase text-gray-500">
                      <tr>
                        <th className="px-3 py-2">Period Start</th>
                        <th className="px-3 py-2">Period End</th>
                        <th className="px-3 py-2">Pay Date</th>
                        <th className="px-3 py-2">Status</th>
                        <th className="px-3 py-2"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {cyclePeriods.map((p) => (
                        <tr key={p.id}>
                          <td className="px-3 py-2 font-mono tabular-nums text-gray-700">
                            {p.period_start}
                          </td>
                          <td className="px-3 py-2 font-mono tabular-nums text-gray-700">
                            {p.period_end}
                          </td>
                          <td className="px-3 py-2 font-mono tabular-nums text-gray-700">
                            {p.pay_date}
                          </td>
                          <td className="px-3 py-2">
                            <StatusBadge status={p.status} />
                          </td>
                          <td className="px-3 py-2 text-right">
                            <div className="flex items-center justify-end gap-2">
                              {p.status !== "exported" && p.status !== "paid" && (
                                <button
                                  type="button"
                                  onClick={async () => {
                                    await api.post(
                                      `/api/payroll/periods/${p.id}/export`,
                                    );
                                    void loadPeriods(c.id);
                                  }}
                                  className="text-xs text-pme-blue hover:underline"
                                >
                                  Export
                                </button>
                              )}
                              {p.status === "exported" && (
                                <button
                                  type="button"
                                  onClick={() => void markPaid(p.id, c.id)}
                                  className="text-xs text-green-600 hover:underline"
                                >
                                  Mark Paid
                                </button>
                              )}
                              {p.status === "paid" && (
                                <span className="text-[10px] text-gray-400">
                                  Done
                                </span>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                      {cyclePeriods.length === 0 && (
                        <tr>
                          <td
                            colSpan={5}
                            className="px-3 py-4 text-center text-xs text-gray-400"
                          >
                            No periods generated yet.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        );
      })}

      {/* Delete confirmation modal */}
      {deleteTarget && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
          onClick={() => setDeleteTarget(null)}
        >
          <div
            className="w-full max-w-md rounded-xl bg-white p-6 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-semibold text-gray-900">
              Delete payroll cycle
            </h3>
            <p className="mt-2 text-sm text-gray-600">
              Are you sure you want to delete{" "}
              <span className="font-semibold">{deleteTarget.name}</span>? All
              associated periods will also be deleted. This cannot be undone.
            </p>
            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setDeleteTarget(null)}
                className="rounded-lg border border-gray-300 px-4 py-2 text-sm"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => void confirmDelete()}
                disabled={deleting}
                className="rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white disabled:opacity-60"
              >
                {deleting ? "Deleting..." : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const styles: Record<string, string> = {
    upcoming: "bg-gray-100 text-gray-600",
    in_progress: "bg-amber-50 text-amber-700",
    calculating: "bg-blue-50 text-blue-700",
    exported: "bg-blue-50 text-blue-700",
    paid: "bg-green-50 text-green-700",
  };
  const labels: Record<string, string> = {
    upcoming: "Upcoming",
    in_progress: "In Progress",
    calculating: "Calculating",
    exported: "Exported",
    paid: "Paid",
  };
  return (
    <span
      className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${styles[status] ?? "bg-gray-100 text-gray-600"}`}
    >
      {labels[status] ?? status}
    </span>
  );
}

function CycleForm({
  cycle,
  sites,
  onSaved,
  onCancel,
}: {
  cycle: Cycle | null;
  sites: Site[];
  onSaved: (c: Cycle) => void;
  onCancel: () => void;
}) {
  const isEdit = !!cycle;

  const [siteId, setSiteId] = useState<number | "">(
    cycle?.site_id ?? sites[0]?.id ?? "",
  );
  const [name, setName] = useState(cycle?.name ?? "");
  const [cycleType, setCycleType] = useState(cycle?.cycle_type ?? "monthly");
  const [description, setDescription] = useState(cycle?.description ?? "");
  const [isActive, setIsActive] = useState(cycle?.is_active ?? true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const body = {
        site_id: Number(siteId),
        name,
        cycle_type: cycleType,
        description: description || null,
        is_active: isActive,
      };
      let result: Cycle;
      if (isEdit && cycle) {
        result = await api.put<Cycle>(
          `/api/payroll/cycles/${cycle.id}`,
          body,
        );
      } else {
        result = await api.post<Cycle>("/api/payroll/cycles", body);
      }
      onSaved(result);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Save failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form
      onSubmit={submit}
      className="space-y-3 rounded-xl border border-gray-200 bg-white p-5"
    >
      <h3 className="text-sm font-semibold text-gray-900">
        {isEdit ? `Edit ${cycle?.name}` : "New Payroll Cycle"}
      </h3>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <label className="block text-sm">
          <span className="text-xs font-medium text-gray-700">Site</span>
          <select
            value={siteId}
            onChange={(e) => setSiteId(Number(e.target.value))}
            className="input mt-1"
          >
            {sites.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>
        </label>
        <label className="block text-sm">
          <span className="text-xs font-medium text-gray-700">Cycle Name</span>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            placeholder="UK Monthly Salaried"
            className="input mt-1"
          />
        </label>
      </div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <label className="block text-sm">
          <span className="text-xs font-medium text-gray-700">Cycle Type</span>
          <DropdownSelect
            listCode="pay_schedule"
            value={cycleType}
            onChange={setCycleType}
            className="input mt-1"
          />
        </label>
        <label className="block text-sm">
          <span className="text-xs font-medium text-gray-700">Description</span>
          <input
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Optional notes"
            className="input mt-1"
          />
        </label>
      </div>
      <label className="flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={isActive}
          onChange={(e) => setIsActive(e.target.checked)}
        />
        <span className="text-xs font-medium text-gray-700">Active</span>
      </label>
      {error && <p className="text-sm text-red-600">{error}</p>}
      <div className="flex gap-2">
        <button
          type="submit"
          disabled={submitting}
          className="rounded-lg bg-pme-blue px-4 py-2 text-sm font-semibold text-white disabled:opacity-60"
        >
          {submitting ? "Saving..." : isEdit ? "Save Changes" : "Create"}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="rounded-lg border border-gray-300 px-4 py-2 text-sm"
        >
          Cancel
        </button>
      </div>
    </form>
  );
}

function siteName(id: number, sites: Site[]): string {
  return sites.find((s) => s.id === id)?.name ?? `Site #${id}`;
}
